import {
  PeriodChip,
  ReportPeriod,
  ReportType,
  TypeChip,
} from "@/components/common/report-chip";
import { cn } from "@/lib/utils";
import { Avatar } from "@/toggle-ui-kit/avatar";

// ============================================
// 타입 정의
// ============================================

/** "최근 수정한 DSD 워크북" 카드가 표시하는 최소 데이터 (표현용 DTO) */
export interface RecentReport {
  id: string;
  name: string;
  /** 재무제표 유형 (연결/별도) */
  financialStatementType?: ReportType;
  /** 보고 기간 (사업/반기/분기) */
  reportPeriod?: ReportPeriod;
  /** 표시 버전 (예: "V3") */
  version: string;
  author: { name: string; email: string };
  /** 상대 수정 시각 (예: "방금 수정") */
  lastModified: string;
}

interface RecentReportCardProps {
  report: RecentReport;
  /** 작성자 이름 조회 중이면 이메일 대신 스켈레톤을 보여준다. */
  isAuthorLoading?: boolean;
  /** `compact` 는 좁은 패널(홈 STEP4 목록)용 — 칩·타이포가 작고 패딩·배경이 없다. */
  size?: "default" | "compact";
  onClick?: () => void;
  className?: string;
}

// ============================================
// RecentReportCard
// 최근 수정한 DSD 워크북 한 장을 렌더링하는 순수 표현 컴포넌트.
// 데이터 조회는 상위(container)가 책임진다.
// ============================================

export const RecentReportCard = ({
  report,
  isAuthorLoading,
  size = "default",
  onClick,
  className,
}: RecentReportCardProps) => {
  const {
    name,
    financialStatementType,
    reportPeriod,
    version,
    author,
    lastModified,
  } = report;
  const isCompact = size === "compact";

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex flex-col items-start gap-2 rounded-lg",
        "text-left",
        "transition-colors hover:bg-neutral-50 active:bg-neutral-100",
        isCompact ? "w-full" : "p-4 bg-white",
        className,
      )}
    >
      {/* 상단: 유형 뱃지 + 파일명 */}
      <div className="flex items-center gap-2 w-full">
        <div className="flex items-center gap-1 shrink-0">
          {financialStatementType && (
            <TypeChip
              type={financialStatementType}
              size={isCompact ? "small" : "large"}
            />
          )}
          {reportPeriod && (
            <PeriodChip
              period={reportPeriod}
              size={isCompact ? "small" : "large"}
            />
          )}
        </div>
        <span
          className={cn(
            "flex-1 min-w-0 text-label-normal truncate",
            isCompact ? "label-2-medium" : "body-2-normal-medium",
          )}
        >
          {name}
        </span>
      </div>

      {/* 하단: 버전 · 작성자 · 수정 시각 */}
      <div className="flex items-center gap-4 caption-1-medium text-label-alternative">
        <span className="whitespace-nowrap">{version}</span>
        <div className="flex min-w-0 items-center gap-1">
          {isAuthorLoading ? (
            <span className="h-4 w-12 animate-pulse rounded bg-neutral-200" />
          ) : (
            <>
              <Avatar
                displayName={author.name}
                email={author.email}
                size="16"
              />
              <span className="truncate">{author.name}</span>
            </>
          )}
        </div>
        <span className="whitespace-nowrap">{lastModified}</span>
      </div>
    </button>
  );
};
